/* [PATCH opombe-stranka-fe v1] [PATCH opombe-osvezevanje v1] Nit opomb racunovodje za stranko na enem racunu.
   Nacrt: claude/NACRT_opombe_za_stranko_v2_20260930.md (odlocitve Janeza 30.09.2026).
   - racunovodja: nova opomba, odgovor v niti, Razresi / Znova odpri
   - stranka: vidi niti (ce obstajajo) in odgovori v nerazreseni niti; nove niti ne odpira
     (za to ima »Opomba za racunovodstvo« = documents.note)
   Po vsaki spremembi: window event 'opombe-spremenjene' (znacka, okno, oznake v seznamu). */
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { api } from '../../lib/api';

export const OPOMBE_DOGODEK = 'opombe-spremenjene';
export const sporociSpremembo = () => { try { window.dispatchEvent(new Event(OPOMBE_DOGODEK)); } catch { /* */ } };

const fmtCas = (iso?: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  const p = (x: number) => String(x).padStart(2, '0');
  return p(d.getDate()) + '.' + p(d.getMonth() + 1) + '.' + d.getFullYear() + ' ' + p(d.getHours()) + ':' + p(d.getMinutes());
};

const STATUS: Record<string, { napis: string; cls: string }> = {
  odprta: { napis: 'Čaka stranko', cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' },
  odgovorjena: { napis: 'Stranka je odgovorila', cls: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300' },
  razresena: { napis: 'Razrešeno', cls: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400' },
};

function Sporocilo({ s }: { s: any }) {
  const jeS = s.avtor_vloga === 'stranka';
  return (
    <div className={'rounded-lg px-3 py-2 text-sm ' + (jeS
      ? 'bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 ml-6'
      : 'bg-amber-50/60 dark:bg-amber-950/30 border border-amber-200/70 dark:border-amber-800/60')}>
      <div className="text-[11px] text-gray-500 dark:text-gray-400 mb-0.5">
        {jeS ? 'Stranka' : 'Računovodstvo'}{s.avtor ? ' · ' + s.avtor : ''} · {fmtCas(s.created_at)}
      </div>
      <div className="whitespace-pre-wrap text-gray-800 dark:text-gray-100">{s.besedilo}</div>
    </div>
  );
}

export function OpombeNit({ companyId, docId, jeStranka, kompaktno }: {
  companyId: string; docId: string; jeStranka: boolean; kompaktno?: boolean;
}) {
  const [niti, setNiti] = useState<any[]>([]);
  const [nalozeno, setNalozeno] = useState(false);
  const [nova, setNova] = useState('');
  const [odprtaNova, setOdprtaNova] = useState(false);
  const [odgovori, setOdgovori] = useState<Record<string, string>>({});
  const [dela, setDela] = useState(false);
  const [pokaziRazresene, setPokaziRazresene] = useState(false);

  const pot = jeStranka
    ? `/stranka/${companyId}/opombe/dokument/${docId}`
    : `/opombe/${companyId}/dokument/${docId}`;

  const nalozi = async () => {
    if (!companyId || !docId) return;
    try { const r = await api.get(pot); setNiti(r.data || []); } catch { setNiti([]); }
    setNalozeno(true);
  };
  useEffect(() => { setNalozeno(false); setNiti([]); setNova(''); setOdprtaNova(false); setOdgovori({}); nalozi(); /* eslint-disable-next-line */ }, [companyId, docId, jeStranka]);
  /* [PATCH opombe-osvezevanje v1] odgovor druge strani brez klika: poll 5 s (samo vidno okno) + fokus okna + dogodek */
  useEffect(() => {
    if (!companyId || !docId) return;
    const osvezi = () => { if (document.visibilityState === 'visible') nalozi(); };
    const t = setInterval(osvezi, 5000);
    window.addEventListener('focus', osvezi);
    document.addEventListener('visibilitychange', osvezi);
    window.addEventListener(OPOMBE_DOGODEK, osvezi);
    return () => {
      clearInterval(t);
      window.removeEventListener('focus', osvezi);
      document.removeEventListener('visibilitychange', osvezi);
      window.removeEventListener(OPOMBE_DOGODEK, osvezi);
    };
    // eslint-disable-next-line
  }, [companyId, docId, jeStranka]);

  const posli = async (besedilo: string, nitId?: string) => {
    const b = (besedilo || '').trim();
    if (!b) { toast.error('Vpiši besedilo'); return; }
    setDela(true);
    try {
      const r = await api.post(pot, { besedilo: b, nit_id: nitId || null });
      setNiti(r.data || []);
      if (nitId) setOdgovori((o) => ({ ...o, [nitId]: '' })); else { setNova(''); setOdprtaNova(false); }
      toast.success(jeStranka ? 'Odgovor poslan računovodstvu' : 'Opomba poslana stranki');
      sporociSpremembo();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Napaka pri shranjevanju opombe');
    } finally { setDela(false); }
  };

  const stanje = async (nitId: string, akcija: 'razresi' | 'odpri') => {
    setDela(true);
    try {
      const r = await api.put(`/opombe/${companyId}/${nitId}/${akcija}`);
      setNiti(r.data || []);
      sporociSpremembo();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Napaka');
    } finally { setDela(false); }
  };

  if (!nalozeno) return null;
  const aktivne = niti.filter((n) => n.status !== 'razresena');
  const razresene = niti.filter((n) => n.status === 'razresena');
  if (jeStranka && niti.length === 0) return null;

  const nitBlok = (n: any) => (
    <div key={n.id} className="space-y-1.5">
      <div className="flex items-center gap-2">
        <span className={'text-[11px] px-2 py-0.5 rounded-full font-medium ' + (STATUS[n.status]?.cls || '')}>{STATUS[n.status]?.napis || n.status}</span>
        {n.status === 'razresena' && n.razresil && <span className="text-[11px] text-gray-400">{n.razresil} · {fmtCas(n.razreseno_at)}</span>}
        {!jeStranka && (
          <button type="button" disabled={dela} onClick={() => stanje(n.id, n.status === 'razresena' ? 'odpri' : 'razresi')}
            className="ml-auto text-xs px-2 py-1 rounded-md border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200 disabled:opacity-50">
            {n.status === 'razresena' ? 'Znova odpri' : 'Razreši'}
          </button>
        )}
      </div>
      <Sporocilo s={n} />
      {(n.odgovori || []).map((o: any) => <Sporocilo key={o.id} s={o} />)}
      {n.status !== 'razresena' && (
        <div className="flex gap-2 items-end">
          <textarea rows={2} value={odgovori[n.id] || ''} onChange={(e) => setOdgovori((o) => ({ ...o, [n.id]: e.target.value }))}
            placeholder={jeStranka ? 'Odgovori računovodstvu …' : 'Dopiši stranki …'}
            className="flex-1 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white text-sm" />
          <button type="button" disabled={dela || !(odgovori[n.id] || '').trim()} onClick={() => posli(odgovori[n.id] || '', n.id)}
            className="px-3 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium disabled:opacity-50">
            {jeStranka ? 'Odgovori' : 'Pošlji'}
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className={(kompaktno ? '' : 'mt-3 ') + 'p-3 rounded-xl border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/40 space-y-3'}>
      <div className="flex items-center gap-2">
        <div className="font-semibold text-amber-800 dark:text-amber-300 text-sm">
          {jeStranka ? 'Računovodstvo prosi za ureditev' : 'Opombe za stranko'}
        </div>
        {!jeStranka && !odprtaNova && (
          <button type="button" onClick={() => setOdprtaNova(true)}
            className="ml-auto text-xs px-2 py-1 rounded-md bg-amber-600 hover:bg-amber-700 text-white font-medium">
            + Opomba za stranko
          </button>
        )}
      </div>
      {!jeStranka && odprtaNova && (
        <div className="space-y-2">
          <textarea rows={3} value={nova} onChange={(e) => setNova(e.target.value)} autoFocus
            placeholder="Npr. manjka priloga / napačen znesek / kam spada ta strošek …"
            className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white text-sm" />
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={() => { setOdprtaNova(false); setNova(''); }}
              className="px-3 py-1.5 rounded-lg text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800">Prekliči</button>
            <button type="button" disabled={dela || !nova.trim()} onClick={() => posli(nova)}
              className="px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium disabled:opacity-50">Pošlji stranki</button>
          </div>
        </div>
      )}
      {aktivne.map(nitBlok)}
      {!jeStranka && aktivne.length === 0 && !odprtaNova && razresene.length === 0 && (
        <div className="text-xs text-gray-500 dark:text-gray-400">Ni opomb. Stranka opombo vidi ob vstopu v aplikacijo in lahko odgovori.</div>
      )}
      {razresene.length > 0 && (
        <div>
          <button type="button" onClick={() => setPokaziRazresene((v) => !v)} className="text-xs text-gray-500 dark:text-gray-400 hover:underline">
            {pokaziRazresene ? 'Skrij' : 'Pokaži'} razrešene ({razresene.length})
          </button>
          {pokaziRazresene && <div className="mt-2 space-y-3 opacity-80">{razresene.map(nitBlok)}</div>}
        </div>
      )}
    </div>
  );
}
